import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { ArrowLeft, Upload, RefreshCw, FileText, Search, Trash2, ChevronDown, ChevronUp, ArrowUpDown } from 'lucide-react';
import { toast } from 'sonner';

const VarietyReports = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [expandedCats, setExpandedCats] = useState({});
  const [allExpanded, setAllExpanded] = useState(false);
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'cat-az');
  const [itemSort, setItemSort] = useState({ field: 'name', dir: 'asc' });
  const fileInputRef = React.useRef(null);
  const isMounted = useRef(false);

  useEffect(() => { fetchReports(); }, []);

  // Sync filters → URL silently (bypasses React Router history so back nav is unaffected)
  useEffect(() => {
    if (!isMounted.current) { isMounted.current = true; return; }
    const url = new URL(window.location.href);
    if (searchQuery) url.searchParams.set('q', searchQuery); else url.searchParams.delete('q');
    if (sortBy && sortBy !== 'cat-az') url.searchParams.set('sort', sortBy); else url.searchParams.delete('sort');
    window.history.replaceState(window.history.state, '', url.toString());
  }, [searchQuery, sortBy]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const res = await api.get('/variety-reports/reports');
      setReports(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/variety-reports/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Uploaded! ${res.data.total_items} items, ${res.data.total_categories} categories`);
      fetchReports();
      viewReport(res.data.report_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const viewReport = async (reportId) => {
    try {
      const res = await api.get(`/variety-reports/reports/${reportId}`);
      setSelectedReport(res.data);
      setReportData(res.data.items || []);
      setExpandedCats({});
    } catch (e) { toast.error('Failed to load report'); }
  };

  const deleteReport = async (reportId) => {
    if (!window.confirm('Delete this report?')) return;
    try {
      await api.delete(`/variety-reports/reports/${reportId}`);
      toast.success('Report deleted');
      if (selectedReport?._id === reportId) { setSelectedReport(null); setReportData(null); }
      fetchReports();
    } catch (e) { toast.error('Failed'); }
  };

  const toggleCat = (cat) => setExpandedCats(prev => ({ ...prev, [cat]: !prev[cat] }));

  const toggleAll = () => {
    const newState = !allExpanded;
    setAllExpanded(newState);
    const updated = {};
    Object.keys(groupedItems).forEach(cat => { updated[cat] = newState; });
    setExpandedCats(updated);
  };

  // Group items by category
  const groupedItems = {};
  (reportData || []).forEach(item => {
    const cat = item.category || 'Unknown';
    if (!groupedItems[cat]) groupedItems[cat] = [];
    groupedItems[cat].push(item);
  });

  const filteredCategories = Object.keys(groupedItems).filter(cat => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    if (cat.toLowerCase().includes(q)) return true;
    return groupedItems[cat].some(i => i.stone_name?.toLowerCase().includes(q));
  }).sort((a, b) => {
    const aItems = groupedItems[a];
    const bItems = groupedItems[b];
    const aClosing = aItems.reduce((s, i) => s + (i.closing_wt || 0), 0);
    const bClosing = bItems.reduce((s, i) => s + (i.closing_wt || 0), 0);
    switch (sortBy) {
      case 'cat-za': return b.localeCompare(a);
      case 'stock-desc': return bClosing - aClosing;
      case 'stock-asc': return aClosing - bClosing;
      case 'count-desc': return bItems.length - aItems.length;
      default: return a.localeCompare(b); // cat-az
    }
  });

  // Report list view
  if (!selectedReport) {
    return (
      <div className="h-screen flex flex-col bg-gray-50" data-testid="variety-reports-page">
        <div className="bg-[#075E54] text-white px-4 py-3 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" className="text-white hover:bg-emerald-700" onClick={() => navigate(-1)}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-lg font-semibold">Variety Reports</h1>
                <p className="text-xs text-emerald-200">{reports.length} reports</p>
              </div>
            </div>
            <div>
              <input type="file" ref={fileInputRef} accept=".pdf" onChange={handleUpload} className="hidden" />
              <Button size="sm" className="bg-white text-emerald-700 hover:bg-emerald-50 h-8" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                {uploading ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                Upload PDF
              </Button>
            </div>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-4 pt-3 pb-4 space-y-2">
          {loading ? (
            <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-emerald-600" /></div>
          ) : reports.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No reports yet. Upload a Stone Transaction PDF.</p>
            </div>
          ) : reports.map(r => (
            <div key={r._id} className="bg-white rounded-lg border p-3 cursor-pointer hover:shadow-md transition-shadow" onClick={() => viewReport(r._id)}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-bold text-sm text-gray-900">{r.filename}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{r.total_items} items, {r.total_categories} categories</p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {r.uploaded_by_name} — {new Date(r.uploaded_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </p>
                </div>
                <button onClick={(e) => { e.stopPropagation(); deleteReport(r._id); }} className="p-1 text-gray-300 hover:text-red-500">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Report detail view
  return (
    <div className="h-screen flex flex-col bg-gray-50" data-testid="variety-report-detail">
      <div className="bg-[#075E54] text-white px-4 py-3 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="text-white hover:bg-emerald-700" onClick={() => { setSelectedReport(null); setReportData(null); }}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-base font-semibold truncate">{selectedReport.filename}</h1>
              <p className="text-xs text-emerald-200">{selectedReport.total_items} items, {selectedReport.total_categories} categories</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search + Collapse/Expand */}
      <div className="px-4 pt-3 flex gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input placeholder="Search category or stone..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 h-9" />
        </div>
        <Button size="sm" variant="outline" className="h-9 text-xs flex-shrink-0" onClick={toggleAll}>
          {allExpanded ? <ChevronUp className="w-3.5 h-3.5 mr-1" /> : <ChevronDown className="w-3.5 h-3.5 mr-1" />}
          {allExpanded ? 'Collapse' : 'Expand'}
        </Button>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="h-9 w-9 p-0 flex items-center justify-center flex-shrink-0">
            <ArrowUpDown className="w-3.5 h-3.5" />
          </SelectTrigger>
          <SelectContent align="end">
            <SelectItem value="cat-az">Category A-Z</SelectItem>
            <SelectItem value="cat-za">Category Z-A</SelectItem>
            <SelectItem value="stock-desc">Stock High-Low</SelectItem>
            <SelectItem value="stock-asc">Stock Low-High</SelectItem>
            <SelectItem value="count-desc">Items High-Low</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Data grouped by category */}
      <div className="flex-1 overflow-y-auto px-4 pt-3 pb-4 space-y-2">
        {filteredCategories.length === 0 ? (
          <p className="text-center py-8 text-gray-400">No matching data</p>
        ) : filteredCategories.map(cat => {
          const items = groupedItems[cat].filter(i => {
            if (!searchQuery) return true;
            return i.stone_name?.toLowerCase().includes(searchQuery.toLowerCase()) || cat.toLowerCase().includes(searchQuery.toLowerCase());
          });
          const totalClosing = items.reduce((sum, i) => sum + (i.closing_wt || 0), 0);
          const isExpanded = expandedCats[cat] === true; // Default collapsed

          return (
            <div key={cat} className="bg-white rounded-lg border overflow-hidden">
              <button onClick={() => toggleCat(cat)} className="w-full px-3 py-2.5 flex items-center justify-between hover:bg-gray-50 transition-colors">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-sm text-gray-900">{cat}</span>
                  <span className="text-[10px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded font-bold">{items.length}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-emerald-700">{totalClosing.toFixed(3)} Ct</span>
                  {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                </div>
              </button>
              {isExpanded && (
                <div className="border-t">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-3 py-1.5 text-left font-semibold text-gray-600 cursor-pointer hover:text-emerald-700 select-none"
                          onClick={(e) => { e.stopPropagation(); setItemSort(prev => ({ field: 'name', dir: prev.field === 'name' && prev.dir === 'asc' ? 'desc' : 'asc' })); }}>
                          Stone Name {itemSort.field === 'name' && (itemSort.dir === 'asc' ? '↑' : '↓')}
                        </th>
                        <th className="px-3 py-1.5 text-right font-semibold text-gray-600 cursor-pointer hover:text-emerald-700 select-none"
                          onClick={(e) => { e.stopPropagation(); setItemSort(prev => ({ field: 'stock', dir: prev.field === 'stock' && prev.dir === 'desc' ? 'asc' : 'desc' })); }}>
                          Closing (Ct) {itemSort.field === 'stock' && (itemSort.dir === 'desc' ? '↓' : '↑')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...items].sort((a, b) => {
                        if (itemSort.field === 'stock') {
                          return itemSort.dir === 'desc' ? (b.closing_wt || 0) - (a.closing_wt || 0) : (a.closing_wt || 0) - (b.closing_wt || 0);
                        }
                        return itemSort.dir === 'asc' ? (a.stone_name || '').localeCompare(b.stone_name || '') : (b.stone_name || '').localeCompare(a.stone_name || '');
                      }).map((item, i) => (
                        <tr key={i} className={i % 2 === 0 ? '' : 'bg-gray-50/50'}>
                          <td className="px-3 py-1.5 font-medium text-gray-800">{item.stone_name}</td>
                          <td className="px-3 py-1.5 text-right text-gray-700">{(item.closing_wt || 0).toFixed(3)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default VarietyReports;
